// AgentNodeModal.js
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

import Modal from '../base/Modal'; // Assuming Modal base component path

const AgentNodeModal = ({
  mode,
  nodeData,
  onClose,
  onSave,
  onDelete,
  agentsList = [], // Default to empty array
  viewOnly,
}) => {
  const [selectedAgentName, setSelectedAgentName] = useState(
    nodeData?.data?.agent_name || '',
  );
  const selectRef = useRef(null);

  // Memoize selectedAgentDetails to avoid re-calculating on every render
  const selectedAgentDetails = React.useMemo(() => {
    return agentsList?.find((agent) => agent.name === selectedAgentName);
  }, [agentsList, selectedAgentName]);

  useEffect(() => {
    // If in edit mode and an agent_name exists but details aren't found (e.g. agentsList changed)
    // it might be desirable to clear the selection or show a warning.
    // For now, we just ensure focus.
    if (!viewOnly && selectRef.current && !selectedAgentName) {
      selectRef.current.focus();
    }
  }, [viewOnly, selectedAgentName]);

  const handleSelectAgent = (agentName) => {
    setSelectedAgentName(agentName);
  };

  const handleSave = () => {
    if (!selectedAgentName || !selectedAgentDetails) {
      // Ensure details are also available
      toast.error('Please select a valid agent.'); // Or use a toast notification
      return;
    }
    const updatedNode = {
      ...nodeData,
      data: {
        ...nodeData?.data, // Preserve other data if any
        agent_name: selectedAgentName,
        agent_description: selectedAgentDetails.description || '', // Save the description
      },
    };
    onSave(updatedNode);
  };

  return (
    <Modal
      title={
        viewOnly
          ? 'Agent Details'
          : mode === 'add'
            ? 'Add Agent Node'
            : 'Edit Agent Node'
      }
      isOpen={true}
      onClose={onClose}
    >
      <div className="h-[auto] max-h-[70vh] overflow-y-auto rounded-lg bg-zinc-900 p-4 shadow-md">
        <div className="p-4">
          <label
            className="mb-2 block text-sm font-medium text-zinc-200"
            htmlFor="agent-select"
          >
            {viewOnly ? 'Selected Agent' : 'Select an Agent'}
          </label>

          {/* Simplified display for selected agent when not in viewOnly mode */}
          {selectedAgentName && !viewOnly ? (
            <div className="mb-4 flex items-center justify-between rounded border border-zinc-700 bg-zinc-800 p-3">
              <span className="font-medium text-zinc-100">
                {selectedAgentName}
              </span>
              <button
                className="rounded bg-zinc-700 px-3 py-1 text-sm text-zinc-300 transition-colors hover:bg-zinc-600"
                onClick={() => setSelectedAgentName('')} // Clear selection to show dropdown again
                aria-label="Change selected agent"
              >
                Change
              </button>
            </div>
          ) : !viewOnly ? (
            <select
              id="agent-select"
              ref={selectRef}
              className="mb-4 w-full rounded border border-zinc-700 bg-zinc-800 p-2.5 text-zinc-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
              onChange={(e) => handleSelectAgent(e.target.value)}
              value={selectedAgentName} // Ensure this is controlled
              aria-label="Select an agent"
            >
              <option value="" disabled={selectedAgentName !== ''}>
                {' '}
                {/* Disable if already selected to prevent accidental reselection of placeholder */}
                -- Select an Agent --
              </option>
              {agentsList.map((agent) => (
                <option key={agent.name} value={agent.name}>
                  {agent.name}
                </option>
              ))}
            </select>
          ) : null}

          {/* Display agent details (name and description) */}
          {selectedAgentDetails && (
            <div className="mb-4">
              <div className="flex w-full flex-col space-y-1 rounded border border-zinc-700 bg-zinc-800 p-3 text-zinc-100 transition-all duration-200">
                <h3 className="text-md font-semibold text-blue-400">
                  {selectedAgentDetails.name}
                </h3>
                <p className="whitespace-pre-wrap text-sm text-zinc-300">
                  {' '}
                  {/* Allow text wrapping for description */}
                  {selectedAgentDetails.description ||
                    'No description available.'}
                </p>
              </div>
            </div>
          )}

          {/* Message if viewing and no details are found (e.g., agent was deleted from list) */}
          {viewOnly && !selectedAgentDetails && selectedAgentName && (
            <p className="text-sm text-yellow-500">
              Details for agent "{selectedAgentName}" are currently unavailable.
              It might have been removed.
            </p>
          )}
          {viewOnly && !selectedAgentName && (
            <p className="text-zinc-400">No agent selected for this node.</p>
          )}

          {!viewOnly && (
            <div className="mt-6 flex justify-end gap-3">
              {mode === 'edit' && (
                <button
                  className="rounded border border-red-500 bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                  onClick={() => onDelete(nodeData.id)}
                  aria-label="Delete agent node"
                >
                  Delete Node
                </button>
              )}
              <button
                className="rounded border border-[#5652CF] bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-[#5652CF] focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
                onClick={handleSave}
                disabled={!selectedAgentName || !selectedAgentDetails} // Disable if no agent or details are missing
                aria-label="Save agent node"
              >
                {mode === 'add' ? 'Add Node' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AgentNodeModal;
